/*
 * @Author: Christion.Young
 * @Date: 2021-09-17 10:04:22
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-08-14 15:20:00
 * @Description: 用户状态管理 - 简化版
 */
import { authAPI } from "@/api";

// 用户状态
const state = {
  token: sessionStorage.getItem("token") || "", // 访问令牌
  refreshToken: sessionStorage.getItem("refreshToken") || "", // 刷新令牌
  userInfo: JSON.parse(
    sessionStorage.getItem("userInfo") ||
      JSON.stringify({
        userId: "",
        username: "",
        nickname: "",
        phone: "",
        avatarUrl: "",
        email: "",
        role: "",
        merchantId: "",
        merchantName: "",
        isStaff: false,
        auditStatus: "",
        permissions: [],
        roles: [],
      })
  ),
  isLoggedIn: !!sessionStorage.getItem("token"), // 登录状态
  forcePasswordChange: false, // 是否需要强制修改密码
  // 添加平台信息字段
  platform: {
    version: "v2.1.0",
    phone: "400-123-4567",
    email: "contact@qrzh.com",
    qualificationUrl: "https://admin.sanhsf.com/images/平台资质.jpg",
  },
  // 新增：平台地域筛选开关
  regionFilterEnabled: false,
  // 仅保存城市中文名
  selectedCityName: sessionStorage.getItem("selectedCityName") || "",
};

// getters
const getters = {
  token: (state) => state.token,
  refreshToken: (state) => state.refreshToken,
  userInfo: (state) => state.userInfo,
  isLoggedIn: (state) => state.isLoggedIn,
  username: (state) => state.userInfo.username,
  nickname: (state) => state.userInfo.nickname,
  avatarUrl: (state) => state.userInfo.avatarUrl,
  role: (state) => state.userInfo.role,
  phone: (state) => state.userInfo.phone,
  email: (state) => state.userInfo.email,
  merchantId: (state) => state.userInfo.merchantId,
  merchantName: (state) => state.userInfo.merchantName,
  isStaff: (state) => state.userInfo.isStaff,
  auditStatus: (state) => state.userInfo.auditStatus,
  permissions: (state) => state.userInfo.permissions || [],
  roles: (state) => state.userInfo.roles || [],
  platform: (state) => state.platform,
  forcePasswordChange: (state) => state.forcePasswordChange,
  regionFilterEnabled: (state) => state.regionFilterEnabled,
  selectedCityName: (state) => state.selectedCityName,
};

// mutations
const mutations = {
  // 设置平台地域筛选开关
  setRegionFilterEnabled(state, value) {
    state.regionFilterEnabled = value;
  },
  // 设置登录状态
  SET_LOGIN_STATUS(state, status) {
    state.isLoggedIn = status;
  },

  // 设置token
  SET_TOKEN(state, tokenData) {
    if (typeof tokenData === "string") {
      // 兼容旧的字符串token格式
      state.token = tokenData;
      if (tokenData) {
        sessionStorage.setItem("token", tokenData);
        state.isLoggedIn = true;
      } else {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("refreshToken");
        state.isLoggedIn = false;
        state.refreshToken = "";
      }
    } else if (tokenData && typeof tokenData === "object") {
      // OAuth2 token对象格式
      state.token = tokenData.access_token;
      state.refreshToken = tokenData.refresh_token || "";

      if (tokenData.access_token) {
        sessionStorage.setItem("token", tokenData.access_token);
        if (tokenData.refresh_token) {
          sessionStorage.setItem("refreshToken", tokenData.refresh_token);
        }
        state.isLoggedIn = true;
      } else {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("refreshToken");
        state.isLoggedIn = false;
      }
    }
  },

  // 设置用户信息
  SET_USER_INFO(state, userInfo) {
    // 处理用户信息数据结构
    const sysUser = userInfo.sysUser;

    // 设置用户信息
    state.userInfo = {
      userId: sysUser.userId || "",
      username: sysUser.username || "",
      nickname: sysUser.nickname || sysUser.username || "",
      email: sysUser.email || "",
      phone: sysUser.phone || "",
      avatarUrl: sysUser.avatar || "",
      role: userInfo.roles ? userInfo.roles[0] : "",
      merchantId: sysUser.merchantId || "",
      merchantName: sysUser.merchantName || "",
      isStaff: sysUser.isStaff || false,
      auditStatus: sysUser.auditStatus || "",
      permissions: userInfo.permissions || [],
      roles: userInfo.roles || [],
    };

    // 保存到sessionStorage
    sessionStorage.setItem("userInfo", JSON.stringify(state.userInfo));
  },

  // 更新用户信息（部分更新）
  UPDATE_USER_INFO(state, updateData) {
    state.userInfo = {
      ...state.userInfo,
      ...updateData,
    };

    // 保存到sessionStorage
    sessionStorage.setItem("userInfo", JSON.stringify(state.userInfo));
  },

  // 清除用户数据
  CLEAR_USER_DATA(state) {
    state.token = "";
    state.refreshToken = "";
    state.userInfo = {
      userId: "",
      username: "",
      nickname: "",
      phone: "",
      avatarUrl: "",
      email: "",
      role: "",
      merchantId: "",
      merchantName: "",
      isStaff: false,
      auditStatus: "",
      permissions: [],
      roles: [],
    };
    state.isLoggedIn = false;
    state.forcePasswordChange = false;
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("userInfo");
  },

  // 设置强制密码修改状态
  SET_FORCE_PASSWORD_CHANGE(state, value) {
    state.forcePasswordChange = value;
  },
  // 设置选中城市中文名
  setSelectedCityName(state, cityName) {
    state.selectedCityName = cityName;
    sessionStorage.setItem("selectedCityName", cityName);
  },
};

// actions
const actions = {
  /**
   * 用户登录 - 使用OAuth2认证
   * @param {Object} context - Vuex context
   * @param {Object} loginForm - 登录表单数据
   * @returns {Promise} 登录结果
   */
  async login({ commit, dispatch }, loginForm) {
    try {
      console.log("开始OAuth2登录:", loginForm.username);

      // 使用真实的OAuth2登录API
      const response = await authAPI.login(loginForm);

      if (response && response.access_token) {
        // OAuth2登录成功，保存token信息
        const tokenData = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          token_type: response.token_type || "Bearer",
          expires_in: response.expires_in,
        };

        // 保存token信息
        commit("SET_TOKEN", tokenData);

        // 登录成功后获取用户信息
        try {
          const userInfoResponse = await authAPI.getUserInfo();
          if (userInfoResponse && userInfoResponse.data) {
            console.log("获取用户信息成功:", userInfoResponse.data);
            console.log(
              "服务器返回的email字段值:",
              userInfoResponse.data.sysUser.email
            );
            console.log(
              "email字段类型:",
              typeof userInfoResponse.data.sysUser.email
            );
            console.log(
              "email是否为空:",
              userInfoResponse.data.sysUser.email === null ||
                userInfoResponse.data.sysUser.email === undefined ||
                userInfoResponse.data.sysUser.email === ""
            );
            commit("SET_USER_INFO", userInfoResponse.data);

            // 检查是否需要强制修改密码
            dispatch("checkForcePasswordChange", userInfoResponse.data);
          }
        } catch (userInfoError) {
          console.warn("获取用户信息失败:", userInfoError);
          // 获取用户信息失败不影响登录流程
        }

        console.log("OAuth2登录成功，token已保存");

        return {
          success: true,
          message: "登录成功",
          data: {
            token: response.access_token,
          },
        };
      } else {
        throw new Error(
          response.error_description || response.message || "登录失败"
        );
      }
    } catch (error) {
      console.error("登录失败:", error);

      // 清除可能的错误状态
      commit("CLEAR_USER_DATA");

      // 处理不同类型的错误
      let errorMessage = "登录失败，请稍后重试";

      if (error.response) {
        // HTTP错误响应
        if (error.response.status === 400) {
          errorMessage = "用户名或密码错误";
        } else if (error.response.status === 401) {
          errorMessage = "认证失败，请检查用户名和密码";
        } else if (error.response.status >= 500) {
          errorMessage = "服务器错误，请稍后重试";
        } else {
          errorMessage =
            error.response.data?.error_description ||
            error.response.data?.message ||
            "登录失败";
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  },

  /**
   * 用户登出
   * @param {Object} context - Vuex context
   * @returns {Promise} 登出结果
   */
  async logOut({ commit }) {
    try {
      console.log("开始登出");

      // 调用后端登出接口
      try {
        await authAPI.logout();
      } catch (error) {
        console.warn("后端登出接口调用失败，继续清除本地数据:", error);
      }

      // 清除本地数据
      commit("CLEAR_USER_DATA");

      console.log("登出成功");

      return {
        success: true,
        message: "登出成功",
      };
    } catch (error) {
      console.error("登出失败:", error);

      // 即使API调用失败，也要清除本地数据
      commit("CLEAR_USER_DATA");

      return {
        success: true,
        message: "登出成功",
      };
    }
  },

  /**
   * 刷新Token
   * @param {Object} context - Vuex context
   * @param {string} refreshToken - 刷新令牌
   * @returns {Promise} 新的token数据
   */
  async refreshToken({ commit, state }, refreshToken) {
    try {
      const tokenToUse = refreshToken || state.refreshToken;

      if (!tokenToUse) {
        throw new Error("没有可用的刷新令牌");
      }

      console.log("开始刷新token");

      const response = await authAPI.refreshToken(tokenToUse);

      if (response && response.access_token) {
        const tokenData = {
          access_token: response.access_token,
          refresh_token: response.refresh_token || tokenToUse,
          token_type: response.token_type || "Bearer",
          expires_in: response.expires_in,
        };

        // 更新token
        commit("SET_TOKEN", tokenData);

        console.log("Token刷新成功");
        return tokenData;
      } else {
        throw new Error("刷新token失败");
      }
    } catch (error) {
      console.error("刷新token失败:", error);

      // 刷新失败，清除用户数据
      commit("CLEAR_USER_DATA");

      throw error;
    }
  },

  /**
   * 检查登录状态
   * @param {Object} context - Vuex context
   */
  checkLoginStatus({ commit, state }) {
    const token = state.token || sessionStorage.getItem("token");
    const refreshToken =
      state.refreshToken || sessionStorage.getItem("refreshToken");

    if (token) {
      // 设置token（兼容字符串格式）
      commit("SET_TOKEN", token);

      // 设置refreshToken
      if (refreshToken && !state.refreshToken) {
        state.refreshToken = refreshToken;
      }

      console.log("检测到有效token，用户已登录");
    } else {
      commit("CLEAR_USER_DATA");
      console.log("未检测到有效token，用户未登录");
    }
  },

  /**
   * 初始化用户状态（应用启动时调用）
   * @param {Object} context - Vuex context
   */
  initUserState({ dispatch }) {
    console.log("初始化用户状态");
    dispatch("checkLoginStatus");
  },

  /**
   * 检查是否需要强制修改密码
   * @param {Object} context - Vuex context
   * @param {Object} userInfo - 用户信息
   */
  checkForcePasswordChange({ commit }, userInfo) {
    const email = userInfo.sysUser ? userInfo.sysUser.email : userInfo.email;

    console.log("检查强制密码修改，email字段:", email, "类型:", typeof email);

    // 检查邮箱是否为空值
    const needsPasswordChange =
      email === null ||
      email === undefined ||
      email === "" ||
      (typeof email === "string" && email.trim() === "");

    console.log("是否需要强制修改密码:", needsPasswordChange);

    if (needsPasswordChange) {
      console.log("触发强制密码修改流程");

      // 通过 Vue 的全局事件总线或者直接操作 DOM 来显示强制修改密码弹窗
      // 这里我们设置一个全局状态来标记需要强制修改密码
      commit("SET_FORCE_PASSWORD_CHANGE", true);

      // 延迟显示提示，确保页面已经渲染完成
      setTimeout(() => {
        // 触发全局事件
        if (window.Vue && window.Vue.prototype.$eventBus) {
          window.Vue.prototype.$eventBus.$emit("forcePasswordChange");
        }

        // 或者直接通过 DOM 操作显示提示
        const event = new CustomEvent("forcePasswordChange", {
          detail: { userInfo },
        });
        window.dispatchEvent(event);
      }, 1000);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
